import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Eye, Image as ImageIcon, Upload, Loader2, X, Plus, Images, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";

// Helper to build correct image URL
const getImageUrl = (url) => {
  if (!url) return "";
  // If it's already a full URL, return as is
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // If it's a relative /api/uploads/ URL, prepend the backend URL
  if (url.startsWith("/api/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  // If it's /uploads/, convert to /api/uploads/
  if (url.startsWith("/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
  return url;
};

export default function AdminPostEdit() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!postId;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectingFor, setSelectingFor] = useState("featured"); // "featured" or "gallery"
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    post_type: "news",
    title: "",
    slug: "",
    excerpt: "",
    content_html: "",
    featured_image_id: "",
    featured_image_url: "",
    gallery_images: [],
    category_ids: [],
    location_scope: "choluteca",
    municipio: "",
    departamento: "",
    video_url: "",
    seo_title: "",
    seo_description: "",
    status: "draft",
  });

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchPost();
    }
  }, [postId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // API returns array directly or object with items
      const cats = Array.isArray(response.data) ? response.data : (response.data.items || []);
      setCategories(cats);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}`, {
      });
      const post = response.data;
      setFormData({
        post_type: post.post_type || "news",
        title: post.title || "",
        slug: post.slug || "",
        excerpt: post.excerpt || "",
        content_html: post.content_html || "",
        featured_image_id: post.featured_image_id || "",
        featured_image_url: post.featured_image_url || "",
        gallery_images: post.gallery_images || [],
        category_ids: post.category_ids || [],
        location_scope: post.location_scope || "choluteca",
        municipio: post.municipio || "",
        departamento: post.departamento || "",
        video_url: post.video_url || "",
        seo_title: post.seo_title || "",
        seo_description: post.seo_description || "",
        status: post.status || "draft",
      });
    } catch (error) {
      toast.error("Error al cargar el post");
      navigate("/admin/posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media?limit=50`, {
      });
      setMediaList(response.data.items);
    } catch (error) {
      console.error("Error fetching media:", error);
    }
  };

  const handleMediaOpen = (type = "featured") => {
    setSelectingFor(type);
    fetchMedia();
    setMediaOpen(true);
  };

  const handleMediaSelect = (media) => {
    // Use the helper to get correct URL
    const imageUrl = getImageUrl(media.url);
    
    if (selectingFor === "gallery") {
      // Add to gallery
      if (!formData.gallery_images.includes(imageUrl)) {
        setFormData({
          ...formData,
          gallery_images: [...formData.gallery_images, imageUrl],
        });
      }
    } else {
      // Set as featured image
      setFormData({
        ...formData,
        featured_image_id: media.media_id,
        featured_image_url: imageUrl,
      });
    }
    setMediaOpen(false);
  };

  const handleRemoveGalleryImage = (index) => {
    const newGallery = [...formData.gallery_images];
    newGallery.splice(index, 1);
    setFormData({ ...formData, gallery_images: newGallery });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten imágenes");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("La imagen no debe superar 10MB");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('title', file.name.replace(/\.[^/.]+$/, ''));

      const response = await axios.post(`${API}/media/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      const newMedia = response.data;
      toast.success("Imagen subida correctamente");
      
      // Refresh media list
      await fetchMedia();
      
      // Auto-select the uploaded image
      handleMediaSelect(newMedia);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error("El título es requerido");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        post_type: formData.post_type,
        title: formData.title,
        slug: formData.slug || undefined,
        excerpt: formData.excerpt || undefined,
        content_html: formData.content_html || undefined,
        featured_image_id: formData.featured_image_id || undefined,
        gallery_images: formData.gallery_images || [],
        category_ids: formData.category_ids || [],
        location_scope: formData.location_scope,
        municipio: formData.municipio || undefined,
        departamento: formData.departamento || undefined,
        video_url: formData.video_url || undefined,
        seo_title: formData.seo_title || undefined,
        seo_description: formData.seo_description || undefined,
        status: formData.status,
      };

      if (isEdit) {
        await axios.put(`${API}/posts/${postId}`, payload, {
        });
        toast.success("Post actualizado");
      } else {
        await axios.post(`${API}/posts`, payload);
        toast.success("Post creado");
      }
      navigate("/admin/posts");
    } catch (error) {
      toast.error("Error al guardar");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="post-edit-form">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/admin/posts")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <h1 className="font-heading text-2xl font-bold text-brand-text">
              {isEdit ? "Editar Post" : "Nuevo Post"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger className="w-36" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="published">Publicado</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={saving} data-testid="save-post-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Contenido</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Título del post"
                    className="mt-1"
                    data-testid="post-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="mi-articulo (se genera automáticamente)"
                    className="mt-1"
                    data-testid="post-slug-input"
                  />
                </div>
                <div>
                  <Label htmlFor="excerpt">Extracto</Label>
                  <Textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="Breve descripción del post"
                    rows={3}
                    className="mt-1"
                    data-testid="post-excerpt-input"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Contenido (HTML)</Label>
                  <Textarea
                    id="content"
                    value={formData.content_html}
                    onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
                    placeholder="<p>Contenido del post...</p>"
                    rows={12}
                    className="mt-1 font-mono text-sm"
                    data-testid="post-content-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* SEO */}
            <Card>
              <CardHeader>
                <CardTitle>SEO</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="seo_title">Título SEO</Label>
                  <Input
                    id="seo_title"
                    value={formData.seo_title}
                    onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                    placeholder="Título para motores de búsqueda"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="seo_description">Descripción SEO</Label>
                  <Textarea
                    id="seo_description"
                    value={formData.seo_description}
                    onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                    placeholder="Descripción para motores de búsqueda"
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Post Type */}
            <Card>
              <CardHeader>
                <CardTitle>Tipo de Post</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={formData.post_type}
                  onValueChange={(value) => setFormData({ ...formData, post_type: value })}
                >
                  <SelectTrigger data-testid="post-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">Noticia</SelectItem>
                    <SelectItem value="destination">Destino</SelectItem>
                    <SelectItem value="experience">Experiencia</SelectItem>
                    <SelectItem value="flavor">Sabor</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="page">Página</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Featured Image */}
            <Card>
              <CardHeader>
                <CardTitle>Imagen Destacada</CardTitle>
              </CardHeader>
              <CardContent>
                {formData.featured_image_url ? (
                  <div className="relative">
                    <img
                      src={getImageUrl(formData.featured_image_url)}
                      alt="Featured"
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-2 right-2"
                      onClick={() => handleMediaOpen("featured")}
                    >
                      Cambiar
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-32 flex-col gap-2"
                    onClick={() => handleMediaOpen("featured")}
                    data-testid="select-image-btn"
                  >
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                    Seleccionar imagen
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Gallery Images */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Images className="h-5 w-5" />
                  Galería de Imágenes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {formData.gallery_images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={getImageUrl(img)}
                        alt={`Gallery ${index + 1}`}
                        className="w-full aspect-square object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveGalleryImage(index)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="aspect-square flex-col gap-2"
                    onClick={() => handleMediaOpen("gallery")}
                  >
                    <Plus className="h-6 w-6 text-gray-400" />
                    <span className="text-xs">Agregar</span>
                  </Button>
                </div>
                {formData.gallery_images.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Agrega imágenes adicionales para mostrar en la galería
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  Categorías
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categories.map((cat) => (
                    <div key={cat.category_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={cat.category_id}
                        checked={formData.category_ids.includes(cat.category_id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({
                              ...formData,
                              category_ids: [...formData.category_ids, cat.category_id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              category_ids: formData.category_ids.filter(id => id !== cat.category_id)
                            });
                          }
                        }}
                      />
                      <label
                        htmlFor={cat.category_id}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {cat.name}
                      </label>
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <p className="text-sm text-gray-500">No hay categorías disponibles</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card>
              <CardHeader>
                <CardTitle>Ubicación</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Ámbito</Label>
                  <Select
                    value={formData.location_scope}
                    onValueChange={(value) => setFormData({ ...formData, location_scope: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="location-scope-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="choluteca">Choluteca</SelectItem>
                      <SelectItem value="honduras">Honduras</SelectItem>
                      <SelectItem value="ambos">Ambos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="municipio">Municipio</Label>
                  <Input
                    id="municipio"
                    value={formData.municipio}
                    onChange={(e) => setFormData({ ...formData, municipio: e.target.value })}
                    placeholder="Ej: Choluteca"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="departamento">Departamento</Label>
                  <Input
                    id="departamento"
                    value={formData.departamento}
                    onChange={(e) => setFormData({ ...formData, departamento: e.target.value })}
                    placeholder="Ej: Choluteca"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Video URL (for video type) */}
            {formData.post_type === "video" && (
              <Card>
                <CardHeader>
                  <CardTitle>Video</CardTitle>
                </CardHeader>
                <CardContent>
                  <Label htmlFor="video_url">URL del Video</Label>
                  <Input
                    id="video_url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    className="mt-1"
                    data-testid="video-url-input"
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </form>

      {/* Media Dialog */}
      <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectingFor === "gallery" ? "Agregar a Galería" : "Seleccionar Imagen Destacada"}
            </DialogTitle>
          </DialogHeader>
          
          {/* Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              id="media-upload-input"
            />
            <label
              htmlFor="media-upload-input"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-10 w-10 text-primary animate-spin" />
                  <span className="text-sm text-gray-600">Subiendo imagen...</span>
                </>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    Haz clic para subir una imagen
                  </span>
                  <span className="text-xs text-gray-500">
                    JPG, PNG, GIF hasta 10MB
                  </span>
                </>
              )}
            </label>
          </div>

          {/* Existing Media Grid */}
          <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
            {mediaList.map((media) => (
              <button
                key={media.media_id}
                type="button"
                onClick={() => handleMediaSelect(media)}
                className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors"
              >
                <img
                  src={getImageUrl(media.url)}
                  alt={media.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
            {mediaList.length === 0 && !uploading && (
              <div className="col-span-full text-center py-8 text-gray-500">
                No hay imágenes. Sube una usando el botón de arriba.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
